"use client";

import { useState, useEffect } from "react";
import { Menu, ChevronDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuTrigger, NavigationMenuContent, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-8">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-4 w-16 bg-muted/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="font-default tracking-widest text-xs uppercase text-foreground/80 hover:bg-secondary hover:text-foreground focus:bg-secondary focus:text-foreground data-[state=open]:bg-secondary transition-colors px-2 py-2 rounded-md bg-transparent border-none">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent className="min-w-[200px] bg-background border border-border/10 shadow-md rounded-md p-2">
                  <div className="flex flex-col gap-1">
                    <NavigationMenuLink asChild>
                      <Link to={item.href || "#"} className="font-default tracking-widest text-xs uppercase text-foreground/80 hover:bg-secondary hover:text-foreground focus:bg-secondary focus:text-foreground block px-2 py-2 rounded-md transition-colors">
                        {item.label}
                      </Link>
                    </NavigationMenuLink>
                    {item.children.map(child => <NavigationMenuLink key={child.id} asChild>
                        <Link to={child.href || "#"} className="font-default tracking-widest text-xs uppercase text-foreground/80 hover:bg-secondary hover:text-foreground focus:bg-secondary focus:text-foreground block px-2 py-2 rounded-md transition-colors">
                          {child.label}
                        </Link>
                      </NavigationMenuLink>)}
                  </div>
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="font-default tracking-widest text-xs uppercase text-foreground/80 hover:bg-secondary hover:text-foreground focus:bg-secondary focus:text-foreground data-[active=true]:bg-secondary transition-colors px-2 py-2 rounded-md">
                  {item.label}
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const toggleExpand = (id: string) => {
    setExpandedItems(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };
  if (loading) return null;
  return <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-foreground/5">
          <Menu className="size-6" />
          <span className="sr-only">開啟選單</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:w-[400px] bg-background border-l border-border/30 p-8 overflow-y-auto">
        <div className="flex flex-col gap-6 mt-12">
          {menuItems.map((item, i) => <div key={item.id} data-index={i} className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <Link to={item.href || "#"} className="font-default tracking-widest text-xl uppercase text-foreground hover:text-primary transition-colors block" onClick={() => setIsOpen(false)}>
                  {item.label}
                </Link>
                {item.children && item.children.length > 0 && <Button variant="ghost" size="icon" onClick={() => toggleExpand(item.id)} className="hover:bg-secondary rounded-md transition-colors">
                    <ChevronDown className={cn("size-5 transition-transform duration-300", expandedItems.includes(item.id) && "rotate-180")} />
                  </Button>}
              </div>
              
              {item.children && item.children.length > 0 && expandedItems.includes(item.id) && <div className="flex flex-col gap-4 pl-4 mt-2 border-l border-border/20">
                  {item.children.map(child => <Link key={child.id} to={child.href || "#"} className="font-default tracking-widest text-lg uppercase text-foreground/70 hover:text-primary transition-colors block" onClick={() => setIsOpen(false)}>
                      {child.label}
                    </Link>)}
                </div>}
            </div>)}
          <div className="mt-8 pt-8 border-t border-border/30">
            <Button variant="outline" className="w-full border-border text-foreground hover:bg-secondary font-default tracking-widest uppercase text-sm h-12 rounded-md" onClick={() => setIsOpen(false)}>
              聯絡我們 Contact
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="264695" id="header" className={`sticky top-0 z-50 w-full transition-all duration-700 bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-lg border-b border-border/10" : "border-b border-transparent"}`}>
      <MenuProvider menu_id="43">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 h-[80px] flex items-center justify-start gap-8 md:gap-12">
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 group flex items-center gap-3 md:gap-4">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685908/img/Red and White Modern Loving is Human Instagram Post.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685908/img/Red and White Modern Loving is Human Instagram Post.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685908/img/Red and White Modern Loving is Human Instagram Post.png-wvc-sizes" alt="Dear EXA" className="h-10 w-auto md:h-12 transition-transform duration-500 group-hover:scale-105" loading="lazy" />
              <div className="flex flex-col items-start justify-center font-serif">
                <span className="text-[10px] md:text-xs tracking-[0.2em] uppercase text-foreground/60 font-light leading-none mb-1">
                  Dear EXA
                </span>
                <span className="text-sm md:text-lg tracking-widest text-foreground font-medium leading-none">
                  親愛的表達藝術
                </span>
              </div>
            </Link>
          </div>

          <div className="flex-shrink-0 ml-auto md:ml-0">
            <DesktopNav />
          </div>

          <div className="flex-1 flex justify-end items-center gap-1">
            <Button variant="outline" className="hidden md:inline-flex border-border text-foreground hover:bg-secondary font-default tracking-widest uppercase text-xs px-6 h-10 rounded-md transition-colors">
              聯絡我們
            </Button>
            <MobileNav />
          </div>
        </div>
      </MenuProvider>
    </section>;
}